import { useRef, useState, type ReactNode } from "react";
import {
  HIDROTEST_KART_SIRASI,
  KARAR_GEREKCELERI,
  KARAR_GEREKCE_ADI,
  KARAR_SECENEK_ADI,
  hatirlaVarsayilan,
  hidrotestEtiketi,
  kartSirasiyla,
  type HidrotestKarti,
  type KararGerekcesi,
  type KararSecenegi,
  type KarsilastirmaNotu,
  type KartUyarisi,
  type Kesinlik,
} from "@oasis/domain";
import { Band, Buton, alan } from "@/components/parcalar";
import { api } from "@/lib/api";
import { KARAR_UCU, OKUMA_UCU, kararMetni, kartUyariMetni, notMetni, okumaDugmesi, okumaEylemleri, tutmaMetni } from "@/lib/belgeler";
import { cn } from "@/lib/cn";
import { cevir } from "@/lib/dil";
import { goruntuleyiciLinki } from "@/lib/goruntuleyici";
import { dosyaYukle, tarih, tarihSaat } from "@/lib/veri";

/**
 * D-074 Faz 2 · PR 7: Documents kartının "Document reading" bölümü (prototip belgeler--hidrotest-okuma). Hidrotest sertifikasının
 * okunan değerleri sabit sırada, kesinlik noktası (yeşil kesin, sarı kontrol, gri okunmadı), sayfa bağlantısı (görüntüleyici) ve
 * "yazıldığı gibi"; beklemeler; okumanın künyesi. Okuma yalnız gösterilir: hiçbir alana dolmaz, hiçbir şey dosyalanmaz; kişi dosyayı
 * bugünkü gibi "File by hand" ile dosyalar. Düğmeler sunucunun hesapladığı eylemlerdir (`evrak` yetkisi); `salt` kapanmış satır içindir
 * (orada yalnız "Remove from reading queue" kalır, `okumaEylemleri`).
 *
 * D-074 Faz 2 · PR 8 (prototip belgeler--hidrotest-uyarilar): "Compared with OASIS" (yer, zaman, notlar) ve "Alerts from the reading" (renge göre;
 * kararlar, sayfa bağlantıları, KIM-02'de hatların değerleri). Kırmızıda karar kutusu (seçenek + K35 gerekçesi; KIM-02'de salt okunur hat ve
 * "Remember for this line"), sarıda "Mark reviewed" ve not; gri yalnız gösterilir. Seçenekleri sunucu kişinin rolüyle hesaplar. "Withdraw
 * decision" hatırlanan KIM-02 kararını geri alır. "Notes from the reading" motorun bulgularıdır (çevrilmez). "Compare again" düğme satırındadır.
 * Karar kayıttır: hiçbir şeyi dosyalamaz, taşımaz; MRB ve OASIS'teki hat SN'si değişmez. `salt` kipte kutular ve düğmeler yok.
 */

const NOKTA: Record<Kesinlik, string> = { kesin: "bg-ok", suphe: "bg-warn", bos: "bg-line-strong" };
const ETIKET_SINIFI = "text-[11.5px] font-semibold uppercase tracking-[0.02em] text-ink-3";

export function AltBaslik({ children }: { children: ReactNode }) {
  return <h4 className={cn("mb-1 mt-3", ETIKET_SINIFI)}>{children}</h4>;
}

export function Nokta({ k }: { k: Kesinlik }) {
  return <span className={cn("mt-1.5 inline-block size-2 shrink-0 rounded-full", NOKTA[k])} aria-hidden />;
}

/** Uyarı kutusunun tonu: kırmızı, sarı, gri; kararlı uyarı gri tonda. */
const UYARI_KUTUSU: Record<KartUyarisi["renk"], string> = { kirmizi: "border-l-bad bg-bad-bg", sari: "border-l-warn bg-warn-bg", gri: "border-l-line-strong bg-surface-2" };
const KOD_RENGI: Record<KartUyarisi["renk"], string> = { kirmizi: "text-bad", sari: "text-warn", gri: "text-ink-3" };

/** Notun noktası: şüpheli değer sarı, öteki notlar gri. */
const notNoktasi = (n: KarsilastirmaNotu): Kesinlik => (n.tur === "suphe" ? "suphe" : "bos");

/** Tek saklı uyarı: cümle, sayfa bağlantıları, açıklama, karar ya da karar kutusu. */
export function UyariSatiri({ u, yer, salt, mesgul, calistir }: { u: KartUyarisi; yer: { rn: string; hat: string } | null; salt?: boolean; mesgul: string | null; calistir: (anahtar: string, f: () => Promise<unknown>) => Promise<void> }) {
  const [gerekce, setGerekce] = useState<KararGerekcesi | "">("");
  const [not, setNot] = useState("");
  /** "Remember for this line": kişi değiştirmediyse gerekçenin varsayılanı (yazım hatasında kapalı). */
  const [hatirlaSecim, setHatirlaSecim] = useState<boolean | null>(null);
  const [yerelHata, setYerelHata] = useState<string | null>(null);
  const hatirla = hatirlaSecim ?? hatirlaVarsayilan(gerekce || null);
  const kirmizi = u.renk === "kirmizi";
  const kutu = !salt && !u.karar && u.secenekler.length > 0;
  const kararVer = (secenek: string) => {
    setYerelHata(null);
    if (kirmizi && !gerekce) { setYerelHata(cevir("Choose a reason.")); return; }
    if ((!kirmizi || gerekce === "diger") && not.trim().length < 3) { setYerelHata(cevir("Write the reason.")); return; }
    const govde = kirmizi
      ? { secenek, gerekce, not: not.trim() || null, ...(u.kod === "KIM-02" && secenek === "hatta_dosyala" ? { hatirla } : {}) }
      : { secenek, not: not.trim() };
    void calistir(`karar-${u.id}`, () => api.post(KARAR_UCU.karar(u.id), govde));
  };
  return (
    <li className={cn("rounded-md border-l-4 px-2.5 py-2 text-sm", u.karar ? UYARI_KUTUSU.gri : UYARI_KUTUSU[u.renk])} data-uyari={u.kod}>
      <p>
        <span className={cn("mr-1.5 font-mono text-xs font-semibold", u.karar ? "text-ink-3" : KOD_RENGI[u.renk])}>{u.kod}</span>
        {kartUyariMetni(u)}
        {u.sayfalar.map((x) => <span key={`${x.dokumanId}-${x.sayfa}`}> <a className="text-xs text-orange underline" href={goruntuleyiciLinki(x.dokumanId, { sayfa: x.sayfa })} target="_blank" rel="noreferrer">p.{x.sayfa}</a></span>)}
      </p>
      {u.aciklama && yer && <p className="mt-0.5 text-xs text-ink-3">{cevir("Lines of riser {0} in OASIS:", yer.rn)} {u.aciklama}</p>}
      {u.karar && (
        <p className="mt-1 text-xs text-ink-2">
          {kararMetni(u.karar)}
          {u.karar.hatirla && u.karar.riserRn && <span className="block">{cevir("Remembered for {0} {1}: {2}", u.karar.riserRn, u.karar.hat ?? "", u.karar.deger ?? "")}</span>}
          {u.karar.geriAl && <span className="block">{cevir("Withdrawn by {0} on {1}", u.karar.geriAl.kim, tarih(u.karar.geriAl.ts))}</span>}
        </p>
      )}
      {!salt && u.geriAlinabilir && u.karar && (
        <Buton kucuk className="mt-1.5" disabled={mesgul !== null} mesgul={mesgul === `geri-${u.karar.id}`} onClick={() => {
          const kararId = u.karar!.id;
          if (window.confirm(cevir("Withdraw the remembered decision? Later documents of this line come red again; earlier files do not change."))) void calistir(`geri-${kararId}`, () => api.post(KARAR_UCU.geriAl(kararId)));
        }}>{cevir("Withdraw decision")}</Buton>
      )}
      {!u.karar && kirmizi && u.secenekler.length === 0 && <p className="mt-1 text-xs text-ink-3">{cevir("Waiting for a decision by the Quality Lead or the PM.")}</p>}
      {kutu && kirmizi && (
        <div className="mt-2 space-y-2 rounded-lg border border-bad! bg-surface p-2.5" data-karar-kutusu={u.kod}>
          <p className="text-sm font-semibold text-bad">{cevir("Decision needed: {0} · {1}", u.kod, cevir("Quality Lead or PM"))}</p>
          {u.kod === "KIM-02" && u.hat && (
            <div>
              <span className={ETIKET_SINIFI}>{cevir("Line")}</span>
              <p className="text-sm">{u.hat}</p>
              <p className="text-xs text-ink-3">{cevir("The line is the place chosen for the file. To file it to another line, change the place: OASIS compares again.")}</p>
            </div>
          )}
          <label className="block">
            <span className={ETIKET_SINIFI}>{cevir("Reason")}</span>
            <select className={cn(alan, "mt-0.5")} aria-label={cevir("Reason")} value={gerekce} disabled={mesgul !== null} onChange={(e) => { setGerekce(e.target.value as KararGerekcesi | ""); setHatirlaSecim(null); }}>
              <option value="">{cevir("Choose a reason...")}</option>
              {KARAR_GEREKCELERI.map((g) => <option key={g} value={g}>{cevir(KARAR_GEREKCE_ADI[g])}</option>)}
            </select>
          </label>
          {u.kod === "KIM-02" && (
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" className="mt-1" checked={hatirla} disabled={mesgul !== null} onChange={(e) => setHatirlaSecim(e.target.checked)} />
              <span>
                {cevir("Remember for this line")}
                <span className="block text-xs text-ink-3">{cevir("Later documents of this line that print the same SN come amber (KIM-07, decided SN). Off when the reason is Misprint on the document.")}</span>
              </span>
            </label>
          )}
          <textarea rows={2} className={alan} value={not} disabled={mesgul !== null} placeholder={cevir("Note (required for Other)")} aria-label={cevir("Note (required for Other)")} onChange={(e) => setNot(e.target.value)} />
          <div className="flex flex-wrap gap-2">
            {u.secenekler.map((x, i) => (
              <Buton key={x} kucuk tur={i === 0 ? "tehlike" : "normal"} disabled={mesgul !== null} mesgul={mesgul === `karar-${u.id}`} onClick={() => kararVer(x)}>{cevir(KARAR_SECENEK_ADI[x as KararSecenegi] ?? x, ...(x === "riser_raporuna_bagla" ? [u.degerler[2] ?? ""] : []))}</Buton>
            ))}
          </div>
          {yerelHata && <p className="text-sm text-bad">{yerelHata}</p>}
        </div>
      )}
      {kutu && !kirmizi && u.secenekler.includes("incelendi") && (
        <div className="mt-2 space-y-2">
          <textarea rows={2} className={alan} value={not} disabled={mesgul !== null} placeholder={cevir("Reason (required)")} aria-label={cevir("Reason (required)")} onChange={(e) => setNot(e.target.value)} />
          <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === `karar-${u.id}`} onClick={() => kararVer("incelendi")}>{cevir("Mark reviewed")}</Buton>
          {yerelHata && <p className="text-sm text-bad">{yerelHata}</p>}
        </div>
      )}
    </li>
  );
}

export function BelgeOkumaBolumu({ satirId, k, onYenile, salt }: { satirId: string; k: HidrotestKarti; onYenile: () => void; salt?: boolean }) {
  const [mesgul, setMesgul] = useState<string | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  /** "Compare again" aynı uyarıları verdiyse bölümde bilgi satırı (T18). */
  const [degismedi, setDegismedi] = useState(false);
  const secici = useRef<HTMLInputElement>(null);
  const o = k.okuma;
  const ey = okumaEylemleri(k, salt);

  const calistir = async (anahtar: string, f: () => Promise<unknown>) => {
    setMesgul(anahtar);
    setHata(null);
    setDegismedi(false);
    try {
      await f();
      onYenile();
    } catch (e) {
      setHata(e instanceof Error ? e.message : cevir("Error"));
    } finally {
      setMesgul(null);
    }
  };
  const degerler = new Map((o?.degerler ?? []).map((d) => [d.alan, d]));

  return (
    <div data-bolum="okuma">
      <h3 className={cn("mb-1.5", ETIKET_SINIFI)}>{cevir("Document reading")}</h3>
      {k.kod && <p className="text-xs text-ink-3">{cevir("Reading job {0}", k.kod)}</p>}
      {k.is.durum === "sirada" && <p className="mt-1 text-sm">{cevir("In the reading queue · place {0}", k.is.siraNo ?? "—")}</p>}
      {k.is.durum === "donusturuluyor" && <p className="mt-1 text-sm">{cevir("The converter is reading this file.")}</p>}
      {k.is.durum === "hata" && (
        <Band tip="not" className="mt-1.5">
          {cevir("Could not read: {0}", cevir(k.is.hata ?? ""))}
          {k.is.hataTs && <span className="mt-0.5 block text-xs">{tarihSaat(k.is.hataTs)}</span>}
        </Band>
      )}
      {!o && !k.is.durum && !salt && <p className="mt-1 text-sm text-ink-2">{cevir("OASIS reads the certificate's values for you to check. Nothing is filed or changed.")}</p>}
      {k.engel && !salt && <Band tip="not" className="mt-1.5">{cevir(k.engel)}</Band>}

      {o && (
        <>
          <AltBaslik>{cevir("Read from the document")}</AltBaslik>
          <ul className="space-y-0.5 text-sm">
            {kartSirasiyla(o.degerler, HIDROTEST_KART_SIRASI).map((d) => (
              <li key={d.alan} className="flex gap-2">
                <Nokta k={d.kesinlik} />
                <span className="w-[11rem] shrink-0 text-ink-2">{cevir(hidrotestEtiketi(d.alan))}</span>
                <span className="min-w-0 [overflow-wrap:anywhere]">
                  {d.deger ?? <span className="text-ink-3">{cevir("not read")}</span>}
                  {d.sayfa !== null && <> <a className="text-xs text-orange underline" href={goruntuleyiciLinki(o.dokumanId, { sayfa: d.sayfa })} target="_blank" rel="noreferrer">p.{d.sayfa}</a></>}
                  {d.yazildigi && d.yazildigi !== d.deger && <span className="block text-xs text-ink-3">{cevir("as written: {0}", d.yazildigi)}</span>}
                </span>
              </li>
            ))}
          </ul>
          {o.tutmalar.length > 0 && (
            <>
              <AltBaslik>{cevir("Holds")}</AltBaslik>
              <ul className="space-y-0.5 text-sm">
                {o.tutmalar.map((t) => (
                  <li key={t.sira} className="flex gap-2">
                    <Nokta k={t.kesinlik} />
                    <span className="min-w-0 [overflow-wrap:anywhere]">
                      {tutmaMetni(t)}
                      {t.sayfa !== null && <> <a className="text-xs text-orange underline" href={goruntuleyiciLinki(o.dokumanId, { sayfa: t.sayfa })} target="_blank" rel="noreferrer">p.{t.sayfa}</a></>}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
          <p className="mt-2 flex flex-wrap gap-3 text-xs text-ink-3">
            <span className="inline-flex items-center gap-1"><Nokta k="kesin" />{cevir("certain")}</span>
            <span className="inline-flex items-center gap-1"><Nokta k="suphe" />{cevir("check")}</span>
            <span className="inline-flex items-center gap-1"><Nokta k="bos" />{cevir("not read")}</span>
          </p>
          <p className="mt-1 text-xs text-ink-3">
            {o.kaynak.tur === "donusturucu"
              ? cevir("Read by the converter on {0} · engine {1}", tarihSaat(o.kaynak.ts), o.motor)
              : cevir("Reading excel uploaded by {0} on {1} · engine {2}", o.kaynak.ad, tarihSaat(o.kaynak.ts), o.motor)}
            {o.sayfalar && <> · {cevir("certificate pages {0}", o.sayfalar.bas === o.sayfalar.son ? String(o.sayfalar.bas) : `${o.sayfalar.bas}-${o.sayfalar.son}`)}</>}
          </p>
          {!salt && <p className="mt-1 text-sm text-ink-2">{cevir("Check the values against the file. Filing is done here by hand, as before.")}</p>}
        </>
      )}

      {o && k.karsilastirma && (
        <div data-bolum="karsilastirma">
          <AltBaslik>{cevir("Compared with OASIS")}</AltBaslik>
          {k.karsilastirma.yer && k.karsilastirma.ts && (
            <p className="text-xs text-ink-3">
              {cevir("Compared for {0} {1} on {2}", k.karsilastirma.yer.rn, k.karsilastirma.yer.hat, tarihSaat(k.karsilastirma.ts))}
              {k.kod && <> · {cevir("Reading job {0}", k.kod)}</>}
            </p>
          )}
          {k.karsilastirma.notlar.length > 0 && (
            <ul className="mt-1 space-y-0.5 text-sm">
              {k.karsilastirma.notlar.map((n, i) => {
                const d = n.tur === "suphe" && n.alan !== "tutma" ? degerler.get(n.alan) : undefined;
                return (
                  <li key={i} className="flex gap-2">
                    <Nokta k={notNoktasi(n)} />
                    <span className="min-w-0">
                      {notMetni(n)}
                      {d && d.sayfa !== null && <> <a className="text-xs text-orange underline" href={goruntuleyiciLinki(o.dokumanId, { sayfa: d.sayfa })} target="_blank" rel="noreferrer">p.{d.sayfa}</a></>}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
          <AltBaslik>{cevir("Alerts from the reading")}</AltBaslik>
          {/* İnceleme bulgusu: PR 8'den önce yazılmış okuma hiç karşılaştırılmadı; "uyarı yok" yanlış güven verirdi. */}
          {!k.karsilastirma.karsilastirildi && k.karsilastirma.uyarilar.length === 0
            ? <Band tip="not">{salt ? cevir("This reading was not compared with OASIS.") : cevir("This reading was not compared with OASIS. Use Compare again to compare it.")}</Band>
            : k.karsilastirma.uyarilar.length === 0
              ? <p className="text-sm text-ink-2">{cevir("No alerts from the comparison.")}</p>
              : (
                <ul className="space-y-1.5">
                  {k.karsilastirma.uyarilar.map((u) => <UyariSatiri key={u.id} u={u} yer={k.karsilastirma!.yer} salt={salt} mesgul={mesgul} calistir={calistir} />)}
                </ul>
              )}
          {k.karsilastirma.bulgular.length > 0 && (
            <>
              <AltBaslik>{cevir("Notes from the reading")}</AltBaslik>
              <ul className="space-y-0.5 text-xs text-ink-3">
                {k.karsilastirma.bulgular.map((b, i) => <li key={i}><span className="mr-1.5 font-mono">{b.kod}</span>{b.mesaj}</li>)}
              </ul>
            </>
          )}
          {degismedi && <p className="mt-1.5 text-sm text-ink-2">{cevir("No change: the comparison gives the same alerts.")}</p>}
        </div>
      )}

      {(ey.gonder || ey.cikar || ey.girdi || ey.karsilastir) && (
        <div className="mt-2 flex flex-wrap gap-2">
          {ey.gonder && (
            <Buton kucuk tur="birincil" disabled={mesgul !== null} mesgul={mesgul === "ekle"} onClick={() => calistir("ekle", () => api.post(OKUMA_UCU.sira(satirId), { islem: "ekle" }))}>
              {cevir(okumaDugmesi(k))}
            </Buton>
          )}
          {ey.karsilastir && (
            <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "karsilastir"} onClick={() => calistir("karsilastir", async () => {
              const r = await api.post<{ ok: true; degisti: boolean }>(OKUMA_UCU.karsilastir(satirId));
              if (!r.degisti) setDegismedi(true);
            })}>
              {cevir("Compare again")}
            </Buton>
          )}
          {ey.cikar && (
            <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "cikar"} onClick={() => calistir("cikar", () => api.post(OKUMA_UCU.sira(satirId), { islem: "cikar" }))}>
              {cevir("Remove from reading queue")}
            </Buton>
          )}
          {ey.girdi && (
            <>
              <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "girdi"} onClick={() => secici.current?.click()}>{cevir("Upload the reading excel by hand")}</Buton>
              <input
                ref={secici}
                type="file"
                accept=".xlsx"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) void calistir("girdi", () => dosyaYukle(OKUMA_UCU.girdi(satirId), f, {}, {}, "excel"));
                }}
              />
            </>
          )}
        </div>
      )}
      {hata && <p className="mt-2 text-sm text-bad">{hata}</p>}
    </div>
  );
}
